// Pages/ResultPage.tsx
import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GradeHistoryItem } from "../types/grading";
import { readLastResult } from "../utils/storage";

type LocationState = { item?: GradeHistoryItem };

export default function ResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const item = useMemo(() => state.item || readLastResult(), [state.item]);

  // Helper function to get recommendation styles
  const getRecommendationStyles = (recommendation: string) => {
    switch (recommendation) {
      case "Hold":
        return "bg-green-100 text-green-800";
      case "Buy":
        return "bg-blue-100 text-blue-800";
      case "Sell":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!item) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-slate-50 py-10">
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-blue-200/60 to-purple-200/60 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-200/60 to-sky-200/60 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] [background-size:22px_22px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No result found
            </h2>
            <p className="text-sm text-slate-600 mb-6">
              Please upload and analyze a card first.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200"
            >
              Go to Upload
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get analysis data from item
  // The item might have analysis object directly or from defects_json
  const analysisData =
    (item as any).analysis ||
    (typeof (item as any).defects_json === "string"
      ? JSON.parse((item as any).defects_json)
      : (item as any).defects_json) ||
    {};

  console.log("Analysis Data:", analysisData);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-50 py-10">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-blue-200/60 to-purple-200/60 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-200/60 to-sky-200/60 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] [background-size:22px_22px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-1">Result</h1>
              <p className="text-sm text-slate-600">
                {new Date(item.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Grade another card
              </button>
              <button
                onClick={() => navigate("/history")}
                className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700">
                Card Image
              </h3>
            </div>
            <div className="p-4">
              <div className="overflow-hidden rounded-xl bg-slate-100">
                <img
                  src={item.image_url}
                  alt="Graded card"
                  className="w-full max-h-[520px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700">
                Grading Results
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Grade and Price */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-slate-600 mb-1">AI Grade</div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 bg-clip-text text-transparent">
                    {analysisData?.grade?.score || item.ai_grade || "N/A"}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-sky-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-slate-600 mb-1">Price</div>
                  <div className="text-2xl font-bold text-indigo-700">
                    {analysisData?.market_price?.graded_price ||
                      item.market_price ||
                      "N/A"}
                  </div>
                  {/* {analysisData?.market_price?.raw_price && (
                  <div className="text-xs text-gray-500 mt-1">
                    Raw: {analysisData.market_price.raw_price}
                  </div>
                )} */}
                </div>
              </div>

              {/* Recommendation */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h4 className="text-sm font-semibold text-gray-700">
                    Recommendation
                  </h4>
                </div>
                <div
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${getRecommendationStyles(
                    analysisData?.recommendation?.action ||
                      item.recommendation ||
                      "Hold",
                  )}`}
                >
                  <span className="mr-2">
                    {(analysisData?.recommendation?.action ||
                      item.recommendation) === "Hold"
                      ? "✅"
                      : (analysisData?.recommendation?.action ||
                            item.recommendation) === "Buy"
                        ? "📈"
                        : "⚠️"}
                  </span>
                  {analysisData?.recommendation?.action ||
                    item.recommendation ||
                    "Hold"}
                  {analysisData?.recommendation?.confidence && (
                    <span className="ml-2 text-xs opacity-75">
                      ({analysisData.recommendation.confidence} confidence)
                    </span>
                  )}
                </div>
              </div>

              {/* Card Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Card Name</div>
                  <div className="text-sm font-medium text-slate-900">
                    {analysisData?.card_name || "Unknown"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Year / Set</div>
                  <div className="text-sm font-medium text-slate-900">
                    {analysisData?.year || "Unknown"} /{" "}
                    {analysisData?.set || "Unknown"}
                  </div>
                </div>
              </div>

              {/* Price Trend */}
              {analysisData?.market_price?.price_trend && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <h4 className="text-sm font-semibold text-slate-700">
                      Price Trend
                    </h4>
                  </div>
                  <span
                    className={`
                    text-xs px-2 py-1 rounded-full
                    ${
                      analysisData.market_price.price_trend === "Rising"
                        ? "bg-green-100 text-green-700"
                        : analysisData.market_price.price_trend === "Declining"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-700"
                    }
                  `}
                  >
                    {analysisData.market_price.price_trend}
                  </span>
                </div>
              )}

              {/* Condition Details */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <h4 className="text-sm font-semibold text-slate-700">
                    Condition Details
                  </h4>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <span className="text-xs text-slate-500">Overall:</span>
                    <p className="font-medium text-slate-900 text-xs mt-1">
                      {analysisData?.grade?.condition || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <span className="text-xs text-slate-500">Centering:</span>
                    <p className="font-medium text-slate-900">
                      {analysisData?.grade?.centering || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <span className="text-xs text-slate-500">Corners:</span>
                    <p className="font-medium text-slate-900">
                      {analysisData?.grade?.corners || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <span className="text-xs text-slate-500">Edges:</span>
                    <p className="font-medium text-slate-900">
                      {analysisData?.grade?.edges || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 col-span-2">
                    <span className="text-xs text-slate-500">Surface:</span>
                    <p className="font-medium text-slate-900">
                      {analysisData?.grade?.surface || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h4 className="text-sm font-semibold text-slate-700">
                    Analysis Reason
                  </h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-4">
                  {analysisData?.recommendation?.reason ||
                    item.ai_reason ||
                    "Analysis complete"}
                </p>
              </div>

              {/* Investment Potential */}
              {analysisData?.recommendation?.investment_potential && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h4 className="text-sm font-semibold text-slate-700">
                      Investment Potential
                    </h4>
                  </div>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                    {analysisData.recommendation.investment_potential}
                  </span>
                </div>
              )}

              {/* Rarity & Demand */}
              {(analysisData?.detailed_analysis?.rarity ||
                analysisData?.detailed_analysis?.market_demand) && (
                <div className="flex gap-4">
                  {analysisData.detailed_analysis.rarity && (
                    <div>
                      <span className="text-xs text-slate-500">Rarity:</span>
                      <p className="text-sm font-medium text-slate-900">
                        {analysisData.detailed_analysis.rarity}
                      </p>
                    </div>
                  )}
                  {analysisData.detailed_analysis.market_demand && (
                    <div>
                      <span className="text-xs text-slate-500">
                        Market Demand:
                      </span>
                      <p className="text-sm font-medium text-slate-900">
                        {analysisData.detailed_analysis.market_demand}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Comparable Sales */}
              {analysisData?.market_price?.comparable_sales && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <h4 className="text-sm font-semibold text-slate-700">
                      Comparable Sales
                    </h4>
                  </div>
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                    {analysisData.market_price.comparable_sales}
                  </p>
                </div>
              )}

              {/* Defects */}
              {analysisData?.grade?.defects &&
                analysisData.grade.defects.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <h4 className="text-sm font-semibold text-slate-700">
                        Detected Defects
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {analysisData.grade.defects.map(
                        (defect: string, idx: number) => (
                          <span
                            key={idx}
                            className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full"
                          >
                            {defect}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
